import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "../../components/layout/Layout";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Upload, PackageSearch, Tag, Loader2, Archive, LayoutDashboard, AlertTriangle, Clock, PackageX, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  en_attente: "bg-slate-500/15 text-slate-500 border-slate-500/30",
  en_cours: "bg-amber-500/15 text-amber-600 border-amber-500/30",
  termine: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
};

// Suivi Chronopost (voir chronopost_tracking.py) — cohérent avec les mêmes
// libellés/couleurs que sur la page de détail d'un bon (DepotOrderDetailPage.jsx).
const TRACKING_STATUS_LABEL = {
  en_preparation: "En préparation chez l'expéditeur",
  depose_relais: "Déposé au relais",
  en_cours_acheminement: "En cours d'acheminement",
  livre: "Livré",
  inconnu: "Statut inconnu",
};
const TRACKING_STATUS_STYLE = {
  en_preparation: "text-slate-600 bg-slate-500/10 border-slate-500/30",
  depose_relais: "text-sky-700 bg-sky-500/10 border-sky-500/30",
  en_cours_acheminement: "text-amber-700 bg-amber-500/10 border-amber-500/30",
  livre: "text-emerald-700 bg-emerald-500/10 border-emerald-500/30",
  inconnu: "text-muted-foreground border-border",
  // Aucun numéro de suivi renseigné du tout — distinct de "inconnu" (qui
  // signifie : un numéro existe, mais Chronopost n'a encore rien à en dire).
  none: "text-muted-foreground/70 border-border/60",
};

// Une commande est "récente" (onglet principal) si elle a moins de 3 jours,
// peu importe son statut — au-delà, elle bascule dans l'onglet "Commandes
// antérieures" pour ne pas noyer l'onglet principal quand il y a beaucoup de
// commandes. Le statut "en_attente"/"en_cours"/"termine" n'entre pas en
// compte ici : une vieille commande encore non terminée finit quand même
// dans les antérieures (elle reste accessible, juste sous un autre onglet).
const RECENT_DAYS = 3;

function isRecent(order) {
  if (!order.created_at) return true; // par prudence, jamais masquer une commande dont on ne connaît pas l'âge
  const ageMs = Date.now() - new Date(order.created_at).getTime();
  return ageMs < RECENT_DAYS * 24 * 60 * 60 * 1000;
}

function totals(order) {
  const total = (order.lines || []).reduce((s, l) => s + l.quantite_attendue, 0);
  const picked = (order.lines || []).reduce((s, l) => s + l.quantite_picked, 0);
  const percent = total > 0 ? Math.round((picked / total) * 100) : 0;
  const lineCount = (order.lines || []).length;
  return { total, picked, percent, lineCount };
}

export default function DepotOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("recent"); // "recent" | "anterieures" | "dashboard"
  const [open, setOpen] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  // Verrou anti double-clic sur le bouton "Créer" : le temps que le serveur
  // extraie le PDF et réponde peut être long, et un second clic pendant ce
  // délai déclenchait la création d'une deuxième commande identique en double.
  const [creating, setCreating] = useState(false);
  const deliveryRef = useRef(null);
  const labelRef = useRef(null);
  const invoiceRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/depot/orders");
    setOrders(data);
  };
  useEffect(() => { load(); }, []);

  const loadDashboard = async () => {
    try {
      const { data } = await api.get("/depot/dashboard");
      setDashboard(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => { if (tab === "dashboard") loadDashboard(); }, [tab]);

  const create = async () => {
    if (creating) return;
    if (!deliveryRef.current.files[0]) {
      toast.error("Le bon de livraison PDF est requis");
      return;
    }
    const fd = new FormData();
    fd.append("delivery", deliveryRef.current.files[0]);
    if (labelRef.current.files[0]) fd.append("label", labelRef.current.files[0]);
    if (invoiceRef.current.files[0]) fd.append("invoice", invoiceRef.current.files[0]);
    setCreating(true);
    try {
      const { data } = await api.post(`/depot/orders`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const invoiceMsg = data.invoice_pdf_url ? ` · ${data.invoice_lines_matched} prix d'achat récupérés depuis la facture` : "";
      const totalQty = (data.lines || []).reduce((s, l) => s + (l.quantite_attendue || 0), 0);
      toast.success(`Commande ${data.numero} créée (${(data.lines || []).length} ligne(s) · ${totalQty} article(s))${invoiceMsg}`);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const recentOrders = orders.filter(isRecent);
  const anteriorOrders = orders.filter((o) => !isRecent(o));
  const visibleOrders = tab === "recent" ? recentOrders : anteriorOrders;

  return (
    <Layout title="Dépôt">
      <div className="flex flex-wrap gap-2 mb-5 no-print">
        <Button
          variant={tab === "recent" ? "default" : "outline"}
          data-testid="depot-tab-orders"
          className="gap-2"
          onClick={() => setTab("recent")}
        >
          <PackageSearch size={16} />Commandes
          {recentOrders.length > 0 && <span className="text-xs opacity-80">({recentOrders.length})</span>}
        </Button>
        <Button
          variant={tab === "anterieures" ? "default" : "outline"}
          data-testid="depot-tab-anterieures"
          className="gap-2"
          onClick={() => setTab("anterieures")}
        >
          <Archive size={16} />Commandes antérieures
          {anteriorOrders.length > 0 && <span className="text-xs opacity-80">({anteriorOrders.length})</span>}
        </Button>
        <Link to="/depot/etiquette">
          <Button variant="outline" data-testid="depot-tab-etiquette" className="gap-2"><Tag size={16} />Étiquettes</Button>
        </Link>
        <Link to="/depot/inventaire">
          <Button variant="outline" data-testid="depot-tab-inventaire" className="gap-2"><FileSpreadsheet size={16} />Inventaire CSV</Button>
        </Link>
        <Button
          variant={tab === "dashboard" ? "default" : "outline"}
          data-testid="depot-tab-dashboard"
          className="gap-2"
          onClick={() => setTab("dashboard")}
        >
          <LayoutDashboard size={16} />Tableau de bord
        </Button>
        <Button data-testid="depot-add-button" onClick={() => setOpen(true)} className="gap-2 sm:ml-auto"><Upload size={16} />Importer un bon de livraison</Button>
      </div>
      {tab === "anterieures" && (
        <p className="text-xs text-muted-foreground mb-4" data-testid="depot-anterieures-hint">
          Commandes de plus de {RECENT_DAYS} jours — elles restent consultables et modifiables ici, elles ne sont simplement plus affichées dans l'onglet principal.
        </p>
      )}

      {tab === "dashboard" && (
        <div className="space-y-5" data-testid="depot-dashboard">
          {!dashboard && <p className="text-sm text-muted-foreground">Chargement...</p>}
          {dashboard && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="rounded-xl border border-border bg-card p-4">
                  <p className="text-xs text-muted-foreground mb-1">Bons en attente d'envoi</p>
                  <p className="text-2xl font-heading font-bold" data-testid="depot-dashboard-en-attente">{dashboard.nb_en_attente_envoi}</p>
                </div>
                <div className={`rounded-xl border p-4 ${dashboard.nb_en_retard > 0 ? "border-destructive/40 bg-destructive/5" : "border-border bg-card"}`}>
                  <p className="text-xs text-muted-foreground mb-1">En retard (plus de {dashboard.seuil_jours_retard} jours)</p>
                  <p className={`text-2xl font-heading font-bold ${dashboard.nb_en_retard > 0 ? "text-destructive" : ""}`} data-testid="depot-dashboard-en-retard">
                    {dashboard.nb_en_retard}
                  </p>
                </div>
                <div className="rounded-xl border border-border bg-card p-4">
                  <p className="text-xs text-muted-foreground mb-1">Délai moyen d'envoi</p>
                  <p className="text-2xl font-heading font-bold" data-testid="depot-dashboard-delai-moyen">
                    {dashboard.delai_moyen_envoi_jours != null ? `${dashboard.delai_moyen_envoi_jours} j` : "—"}
                  </p>
                </div>
              </div>

              {dashboard.bons_en_retard.length > 0 && (
                <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-4">
                  <p className="font-heading font-semibold flex items-center gap-2 mb-3 text-destructive">
                    <AlertTriangle size={16} />Bons en attente depuis trop longtemps
                  </p>
                  <div className="space-y-1.5">
                    {dashboard.bons_en_retard.map((b) => (
                      <Link
                        key={b.id} to={`/depot/${b.id}`}
                        className="flex items-center justify-between text-sm rounded-lg border border-border bg-card px-3 py-2 hover:border-primary transition-colors"
                        data-testid={`depot-dashboard-retard-${b.id}`}
                      >
                        <span className="flex items-center gap-2 min-w-0">
                          <Clock size={13} className="shrink-0 text-muted-foreground" />
                          <span className="truncate">CMD {b.numero} {b.destinataire && `— ${b.destinataire}`}</span>
                        </span>
                        <span className="text-destructive font-medium shrink-0 ml-2">{b.age_jours} j</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              <div className="rounded-xl border border-border bg-card p-4">
                <p className="font-heading font-semibold flex items-center gap-2 mb-3">
                  <PackageX size={16} />Commandes non conformes à traiter
                  {dashboard.nb_non_conformes_a_traiter > 0 && (
                    <span className="text-xs font-normal text-muted-foreground">({dashboard.nb_non_conformes_a_traiter})</span>
                  )}
                </p>
                {dashboard.non_conformes_a_traiter.length === 0 && (
                  <p className="text-sm text-muted-foreground" data-testid="depot-dashboard-non-conformes-empty">Aucune non-conformité en attente — rien à réapprovisionner.</p>
                )}
                <div className="space-y-2">
                  {dashboard.non_conformes_a_traiter.map((c) => (
                    <div key={c.id} className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-sm" data-testid={`depot-dashboard-non-conforme-${c.id}`}>
                      <p className="font-medium">CMD {c.numero} — {c.shop_nom}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {(c.non_conforme_items || []).map((i) => `${i.description} (x${i.quantite_manquante})`).join(", ")}
                      </p>
                      {c.resolution_note && <p className="text-xs text-muted-foreground mt-1 italic">« {c.resolution_note} »</p>}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {tab !== "dashboard" && (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="depot-order-list">
        {visibleOrders.length === 0 && (
          <p className="text-sm text-muted-foreground col-span-full" data-testid="depot-order-list-empty">
            {tab === "recent" ? "Aucune commande récente." : "Aucune commande antérieure."}
          </p>
        )}
        {visibleOrders.map((o) => {
          const { total, picked, percent, lineCount } = totals(o);
          return (
            <Link key={o.id} to={`/depot/${o.id}`} data-testid={`depot-order-${o.id}`} className="rounded-xl border border-border bg-card p-5 min-h-[64px] hover:border-primary transition-colors duration-200">
              <div className="flex items-center justify-between mb-2">
                <p className="font-heading font-semibold flex items-center gap-2 min-w-0"><PackageSearch size={18} className="shrink-0" /><span className="truncate">CMD {o.numero}</span></p>
                <span className={`text-xs px-2 py-1 rounded-md border shrink-0 ${STATUS_STYLE[o.status]}`}>{o.status}</span>
              </div>
              <span
                className={`inline-block text-xs px-2 py-0.5 rounded-full border mb-2 ${TRACKING_STATUS_STYLE[o.tracking_status] || TRACKING_STATUS_STYLE.none}`}
                data-testid={`depot-order-tracking-${o.id}`}
              >
                {o.tracking_number ? (TRACKING_STATUS_LABEL[o.tracking_status] || "Suivi non encore vérifié") : "Aucun numéro de suivi"}
              </span>
              {o.destinataire && <p className="text-xs text-muted-foreground truncate mb-1" data-testid={`depot-order-destinataire-${o.id}`}>{o.destinataire}</p>}
              <p className="text-sm text-muted-foreground">{picked}/{total} · {percent}% <span className="text-muted-foreground/70">· {lineCount} ligne{lineCount > 1 ? "s" : ""}</span></p>
              <div className="h-1.5 rounded-full bg-secondary mt-2 overflow-hidden">
                <div className="h-full bg-primary transition-all duration-300" style={{ width: `${percent}%` }} />
              </div>
            </Link>
          );
        })}
      </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="depot-order-dialog">
          <DialogHeader><DialogTitle>Nouvelle commande à picker</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm text-muted-foreground">Bon de livraison (PDF) — le N° de commande et les lignes sont extraits automatiquement</label>
              <Input data-testid="depot-delivery-input" type="file" accept="application/pdf" ref={deliveryRef} />
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Étiquette Chronopost (optionnel)</label>
              <Input data-testid="depot-label-input" type="file" accept="application/pdf" ref={labelRef} />
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Facture fournisseur (optionnel) — le prix d'achat H.T de chaque article est récupéré automatiquement (lien par UGS avec le bon de livraison)</label>
              <Input data-testid="depot-invoice-input" type="file" accept="application/pdf" ref={invoiceRef} />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="depot-order-create-button" onClick={create} disabled={creating} className="gap-2">
              {creating && <Loader2 size={16} className="animate-spin" />} {creating ? "Création…" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
